import helpers from '@debbie/common/dist/economy/helpers/index.js';
import { formatNumber } from '@debbie/common/dist/index.js';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../../theme.js';
import Alert from '../../Alert.js';
import Checkbox from '../../Checkbox.js';
import Input from '../../Input.js';
import MoneyInput from '../../MoneyInput.js';
var SettlementInputMode;
(function (SettlementInputMode) {
    SettlementInputMode["Percent"] = "percent";
    SettlementInputMode["Total"] = "total";
    SettlementInputMode["Forgive"] = "forgive";
})(SettlementInputMode || (SettlementInputMode = {}));
const SettlementInput = (props) => {
    const lang = useSelector((s) => s.lang);
    const balance = -helpers.overview.balance(props.caseVouchers);
    const { enabled, setEnabled, total, setTotal } = props;
    const [mode, setMode] = useState(SettlementInputMode.Percent);
    // Draft edited by the typed fields; committed to `total` on blur, so the
    // preview holds the last committed value while typing.
    const [totalRaw, setTotalRaw] = useState(total);
    // The percent field keeps its own string draft so it can be emptied —
    // deleting the value leaves a blank field to type a fresh first digit into,
    // not a forced 0. Re-synced from the committed total on blur or mode switch,
    // never mid-keystroke, since typing moves only `totalRaw`, not `total`.
    const [percentStr, setPercentStr] = useState('');
    useEffect(() => {
        setTotalRaw(total);
    }, [total]);
    useEffect(() => {
        setPercentStr(balance > 0 ? String(Math.round((total / balance) * 100)) : '');
    }, [total, mode, balance]);
    const forgiven = Math.max(0, balance - total);
    const percent = balance > 0 ? Math.round((total / balance) * 100) : 0;
    const invalid = enabled && (total <= 0 || total >= balance);
    const commit = () => setTotal(totalRaw);
    const draftForgiven = Math.max(0, balance - totalRaw);
    const summary = lang.paymentPlans.settlementSummary
        .replace('{total}', formatNumber(total))
        .replace('{balance}', formatNumber(balance))
        .replace('{percent}', String(percent));
    return (React.createElement(React.Fragment, null,
        React.createElement(Checkbox, { checked: enabled, onChange: (e) => setEnabled(e.target.checked), placeholder: lang.paymentPlans.settlementEnable }),
        enabled && (React.createElement(React.Fragment, null,
            React.createElement(ModePills, null,
                React.createElement(ModePill, { type: "button", $active: mode === SettlementInputMode.Percent, onClick: () => setMode(SettlementInputMode.Percent) }, lang.paymentPlans.settlementPercentOfBalance),
                React.createElement(ModePill, { type: "button", $active: mode === SettlementInputMode.Total, onClick: () => setMode(SettlementInputMode.Total) }, lang.paymentPlans.settlementAgreedTotal),
                React.createElement(ModePill, { type: "button", $active: mode === SettlementInputMode.Forgive, onClick: () => setMode(SettlementInputMode.Forgive) }, lang.paymentPlans.settlementAmountForgiven)),
            mode === SettlementInputMode.Percent && (React.createElement(Input, { type: "number", min: 1, max: 99, value: percentStr, placeholder: lang.paymentPlans.settlementPercentOfBalance, onChange: (e) => {
                    const next = e.target.value;
                    setPercentStr(next);
                    if (next !== '') {
                        setTotalRaw(Math.max(0, Math.round((balance * Number(next)) / 100)));
                    }
                }, onBlur: commit })),
            mode === SettlementInputMode.Total && (React.createElement(MoneyInput, { value: totalRaw, placeholder: lang.paymentPlans.settlementAgreedTotal, onChange: (amount) => setTotalRaw(amount), onBlur: commit })),
            mode === SettlementInputMode.Forgive && (React.createElement(MoneyInput, { value: draftForgiven, placeholder: lang.paymentPlans.settlementAmountForgiven, onChange: (amount) => setTotalRaw(Math.max(0, balance - amount)), onBlur: commit })),
            React.createElement(SummaryBox, null,
                React.createElement(SummaryRow, null,
                    React.createElement("span", null, lang.paymentPlans.settlementCurrentBalance),
                    React.createElement("b", null, formatNumber(balance))),
                React.createElement(SummaryRow, null,
                    React.createElement("span", null, lang.paymentPlans.settlementAgreedTotal),
                    React.createElement("b", null, formatNumber(total))),
                React.createElement(SummaryRow, null,
                    React.createElement("span", null, lang.paymentPlans.settlementForgivenOnCompletion),
                    React.createElement(ForgivenAmount, null, formatNumber(forgiven))),
                React.createElement(SummarySentence, null, summary)),
            invalid && (React.createElement(Alert, { size: "small", message: lang.paymentPlans.settlementExceedsBalance }))))));
};
export default SettlementInput;
const ModePills = styled.div `
  display: flex;
  gap: 6px;
  margin: 10px 0;
  flex-wrap: wrap;
`;
const ModePill = styled.button `
  border: 1px solid
    ${({ $active }) => ($active ? THEME.COLOR_ACCENT : THEME.BORDER_STRONG)};
  background: ${({ $active }) => ($active ? THEME.COLOR_ACCENT : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#444')};
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  cursor: pointer;
`;
const SummaryBox = styled.div `
  background: #fff;
  border: 1px solid ${THEME.BORDER};
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 10px;
`;
const SummaryRow = styled.div `
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 3px 0;
  color: #444;
`;
const SummarySentence = styled.p `
  margin: 8px 0 0;
  font-size: 12px;
  color: ${THEME.TEXT_MUTED};
`;
const ForgivenAmount = styled.b `
  color: ${THEME.SUCCESS};
`;
//# sourceMappingURL=SettlementInput.js.map